import { Switch } from "@showitinc/showit-wc";
import cx from "classnames";
import React, { useEffect } from "react";
import SiteActions from "@/actions/SiteActions";
import SitePublishActions from "../../actions/SitePublishActions";
import UIActions from "../../actions/UIActions";
import UserActions from "../../actions/UserActions";
import HelpTooltip from "../../components/HelpTooltip";
import { RendererType } from "../../constants/RendererConstants.mjs";
import useRenderer from "../../hooks/query/useRenderer";
import { useSiteState } from "../../hooks/query/useSite";
import styles from "./ProtorendererToggle.module.css";

/**
 * Customer-facing toggle for the protorenderer publish opt-in (persists site_designs.renderer).
 * @param {Object} props
 * @param {string} props.label
 * @param {boolean} [props.showDescription=true] - Show the ON-state warning copy below the switch
 * @param {boolean} [props.showTooltip=true]
 * @returns {React.JSX.Element|null}
 */
const ProtorendererToggle = ({
  label = "Use Beta Publish Engine",
  showDescription = true,
  showTooltip = true,
}) => {
  const site = useSiteState();
  const siteId = site.get("site_design_id");
  const visible = siteId != null;

  const { useProtorenderer, canToggle, isSaving, setProtorenderer, settled } = useRenderer(siteId, {
    enabled: visible,
  });

  /**
   * Persists the flip, then fires analytics from the server's confirmed value.
   * @param {CustomEvent} event
   */
  const _onToggle = async (event) => {
    const next = event?.detail?.value ?? false;
    try {
      const { renderer } = await setProtorenderer(next);
      UserActions.trackPostHogEvent("toggle_flipped", {
        actor: "customer",
        use_protorenderer: renderer === RendererType.V2,
      });
    } catch (err) {
      console.error("Failed to update renderer", err);
    }
  };

  const _openPreview = () => {
    SitePublishActions.closeDialog();
    UIActions.previewOpen();
  };

  useEffect(() => {
    if (visible && settled) {
      SiteActions.updateRendererVersion(useProtorenderer ? 2 : 1);
    }
  }, [settled, useProtorenderer, visible]);

  if (!visible || !canToggle) {
    return null;
  }

  return (
    <div className={styles.toggle} data-testid="protorenderer-toggle">
      <div className={styles["toggle-row"]}>
        <Switch
          className={cx(styles.switch, {
            [styles["switch-active"]]: useProtorenderer,
          })}
          controlled
          checked={useProtorenderer}
          disabled={isSaving}
          label={label}
          onSIChange={_onToggle}
        />
        {showTooltip && (
          <HelpTooltip
            className={styles["toggle-help"]}
            tooltipKey="protorenderer-publish-info"
            placement="top-center"
          />
        )}
      </div>
      {useProtorenderer && showDescription && (
        <div
          className={styles.warning}
          aria-live="polite"
          data-testid="protorenderer-warning"
        >
          Before publishing with the new Publish Engine, please{" "}
          <button
            type="button"
            className={styles["warning-link"]}
            onClick={_openPreview}
          >
            preview your site
          </button>{" "}
          to verify it is displaying as expected.
        </div>
      )}
    </div>
  );
};

export default ProtorendererToggle;
